import { useState, useEffect } from 'react';
import { Row, Col, Space, message, notification } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import intersection from 'lodash/intersection';
import difference from 'lodash/difference';
import { ReModal, ReButton } from "@/components/refineElement";
import ResponsiveTable from '@/components/responsiveTable';
import { showDelConfirm, getOnOkFunction, sortColumn } from "@/lib/antdElementFunction";
import { getLangList } from '@/lib/i18n';
import { getDetailsArray } from "@/lib/user";
import { dateFormat } from "@/lib/dateTime";
import { hasPermission } from "@/lib/permissionUtil";
import StudentConfig from "@/config/student";
import UserConfig from "@/config/user";
import SelfStudyConfig from "@/config/selfStudy";
import { ModalProfile, ModalChangePassword } from "@/components/modal";
import { getUserAccountById } from "@/stores/user/action";
import { StudentType, SelfStudyType, AllPeriodType } from './types';
import ModalChangeClass from './ModalChangeClass';
import ModalSelfStudy from './ModalSelfStudy';

const { updateStudentClass } = StudentConfig;
const { updateUserAccount, changePassword, deleteUserAccount, resetUserPassword } = UserConfig;
const { getAllPeriodInfo, getPeriodInfoById, updateStudyPeriodInfo } = SelfStudyConfig;

type Props = {
  studentArray: StudentType[];
  fetchStudentFunction: Function;
}

function StudentTable(props: Props) {
  const { studentArray, fetchStudentFunction } = props;
  const [studentInfo, setStudentInfo] = useState({} as StudentType);
  const [selfStudyInfo, setSelfStudyInfo] = useState({} as SelfStudyType);
  const [allPeriodArray, setAllPeriodArray] = useState([] as AllPeriodType[]);
  const [isModalChangeClassVisible, setIsModalChangeClassVisible] = useState(false);
  const [isModalProfileVisible, setIsModalProfileVisible] = useState(false);
  const [isModalSelfStudyVisible, setIsModalSelfStudyVisible] = useState(false);
  const [isModalChangePasswordVisible, setIsModalChangePasswordVisible] = useState(false);
  const [handleType, setHandleType] = useState('');
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state: any) => state.user);
  const { userAccountId } = studentInfo;
  const { classType } = selfStudyInfo;
  const studentListT: any = getLangList("studentList");
  const profileInfoT: any = getLangList("profileInfo");
  const errorT: any = getLangList("error");
  const buttonT: any = getLangList("button");
  const errorCodeT: any = getLangList("errorCode");
  const getUserAccountByIdFunction = (userAccountId: number) => () => {
    const params = { userAccountId };
    dispatch(getUserAccountById(params));
  }
  const getUserInfo = handleType === 'updateProfileInfo' ? getUserAccountByIdFunction(userAccountId) : () => { };
  const okUserFunction = {
    updateProfileInfo: updateUserInfoFunction,
  };

  const columns = [
    {
      title: studentListT.SEAT_NO,
      dataIndex: "cramSeat",
      sorter: (a: any, b: any) => sortColumn(a.cramSeat, b.cramSeat),
      sortDirections: ['ascend', 'descend', 'ascend'],
      defaultSortOrder: 'ascend',
    },
    { title: studentListT.ACCOUNT, dataIndex: "username" },
    { title: studentListT.CN_NAME, dataIndex: "nameCn" },
    { title: studentListT.EN_NAME, dataIndex: "nameEn" },
    {
      title: studentListT.OPERATE,
      dataIndex: "action",
      render: (text: any, record: any) => (
        <Space>
          {hasPermission('HR_MANAGEMENT_STUDENT_EDIT') &&
            <ReButton iconName='edit' onClick={clickClassEdit(record)}>
              {studentListT.CHANGE_CLASSROOM}
            </ReButton>
          }

          <ReButton
            iconName='search'
            onClick={clickUserEdit(record)}
          >
            {studentListT.DETAIL_INFO}
          </ReButton>

          <ReButton
            iconName='book'
            onClick={clickSelfStudyEdit(record)}
          >
            {studentListT.SELFSTUDY}
          </ReButton>

          {hasPermission('HR_MANAGEMENT_STUDENT_EDIT') &&
            <ReButton
              iconName='tool'
              onClick={clickPasswordEdit(record)}
              attributeObject={{ type: 'default' }}
            >
              {studentListT.CHANGE_PASSWORD}
            </ReButton>
          }

          {hasPermission('HR_MANAGEMENT_STUDENT_RESET_PASSWORD') &&
            <ReButton
              iconName='tool'
              onClick={clickResetUserPassword(record)}
              attributeObject={{ type: 'default' }}
            >
              {studentListT.RESET_PASSWORD}
            </ReButton>
          }

          {hasPermission('HR_MANAGEMENT_STUDENT_DELETE') &&
            <ReButton iconName='delete' onClick={clickUserDelete(record)} attributeObject={{ type: 'danger' }}>
              {buttonT.DELETE}
            </ReButton>
          }
        </Space>
      ),
    }
  ];

  const openNotification = (password: string) => {
    notification.success({
      message: `${studentListT.RESET_PASSWORD}${errorT.success}`,
      description: `${studentListT.TEMP_PASSWORD}: ${password}`,
      placement: 'top',
      duration: null,
    });
  };

  useEffect(() => {
    getAllPeriodFunction();
  }, []);

  function getAllPeriodFunction() {
    getAllPeriodInfo().then((res: any) => {
      const allPeriodArray = res?.value ?? []
      setAllPeriodArray(allPeriodArray);
    });
  }

  function getPeriodInfoByIdFunction(userAccountId: number) {
    const params = { userAccountId };

    getPeriodInfoById(params).then((res: any) => {
      const selfStudyInfo = res?.value ?? {};
      setSelfStudyInfo(selfStudyInfo);
      setIsModalSelfStudyVisible(!isModalSelfStudyVisible);
    });
  }

  function toggleChangeClassModal(handleType: string = '') {
    setIsModalChangeClassVisible(!isModalChangeClassVisible);
    setHandleType(handleType);
  };

  function toggleProfileModal(handleType: string = '') {
    setIsModalProfileVisible(!isModalProfileVisible);
    setHandleType(handleType);
  };

  const clickClassEdit = (data: StudentType) => (e: any) => {
    e.stopPropagation();
    setStudentInfo(data);
    toggleChangeClassModal('update');
  };

  const clickUserEdit = (data: StudentType) => (e: any) => {
    const { userAccountId } = data;
    e.stopPropagation();
    setStudentInfo(data);
    getUserAccountByIdFunction(userAccountId)();
    toggleProfileModal('updateProfileInfo');
  };

  const clickSelfStudyEdit = (data: StudentType) => (e: any) => {
    const { userAccountId } = data;
    e.stopPropagation();
    getPeriodInfoByIdFunction(userAccountId);
  };

  const clickPasswordEdit = (data: StudentType) => (e: any) => {
    e.stopPropagation();
    setStudentInfo(data);
    setIsModalChangePasswordVisible(!isModalChangePasswordVisible);
  };

  const clickUserDelete = (data: StudentType) => () => {
    showDelConfirm(() => deleteUserAccountFunction(data));
  };

  const clickResetUserPassword = (data: StudentType) => () => {
    showDelConfirm(() => resetUserPasswordFunction(data), buttonT.CONFIRM_RESET_PASSWORD);
  };

  function updateClassFunction(values: any) {
    const { userAccountId } = studentInfo;
    const { newClassId, newSeatNum } = values;
    const params = { userAccountId, newClassId, newSeatNum };

    updateStudentClass(params).then((res: any) => {
      if (!res.success) {
        return message.error(errorT.failed);
      }
      message.success(errorT.success);
      fetchStudentFunction();
      toggleChangeClassModal();
    });
  };

  function updateUserInfoFunction(values: any) {
    const {
      nameCn,
      nameEn,
      accessCardNumber,
      birthday,
      gender,
      residenceAddress,
      mailingAddress,
      identityNumber,
      school,
      grade,
      classroom,
      seatNumber,
      allergy,
      idCardImg,
      qrCode,
      isLunch,
      isSnack,
    } = values;
    const detailsArray = getDetailsArray(values);
    const userInfoData = {
      ...userInfo,
      nameCn,
      nameEn,
      accessCardNumber,
      birthday: birthday?.format(dateFormat) ?? '',
      gender,
      residenceAddress,
      mailingAddress,
      identityNumber,
      school,
      grade,
      classroom,
      seatNumber,
      allergy,
      isLunch: isLunch ? 1 : 0,
      isSnack: isSnack ? 1 : 0,
      details: detailsArray,
    };
    delete userInfoData.idCard;
    delete userInfoData.qrCode;
    const params = { stringFormat: { userInfo: userInfoData }, fileFormat: { idCardImg, qrCode } };

    if (!hasPermission('HR_MANAGEMENT_STUDENT_EDIT')) {
      return message.error(errorT.NO_PERMISSION);
    }

    updateUserAccount(params).then((res: any) => {
      if (!res.success) {
        return message.error(errorCodeT[res.errorCode] ?? errorT.failed);
      }
      message.success(errorT.success);
      fetchStudentFunction();
      toggleProfileModal();
    });
  }

  function updateStudyPeriodFunction(values: any) {
    const { timePeriodIds, userAccountId } = selfStudyInfo;
    const { studyTimeId } = values;
    const intersectionKey = intersection(studyTimeId, timePeriodIds);
    const addStudyTimeId = difference(studyTimeId, intersectionKey);
    const removeStudyTimeId = difference(timePeriodIds, studyTimeId);
    const params = {
      userAccountId,
      addStudyTimeId,
      removeStudyTimeId,
    };

    if (!hasPermission('HR_MANAGEMENT_STUDENT_EDIT')) {
      return message.error(errorT.NO_PERMISSION);
    }

    updateStudyPeriodInfo(params).then((res: any) => {
      if (!res.success) {
        return message.error(errorT.failed);
      }
      message.success(errorT.success);
      setIsModalSelfStudyVisible(!isModalSelfStudyVisible);
    });
  }

  function updatePasswordFunction(data: any) {
    const { username } = studentInfo;
    const { originPassword, newPassword, checkNewPassword } = data;
    const params = { originPassword, newPassword, checkNewPassword, username };

    changePassword(params).then((res: any) => {
      if (!res.success) {
        return message.error(errorT.failed);
      }
      message.success(errorT.success);
      setIsModalChangePasswordVisible(!isModalChangePasswordVisible);
    });
  };

  function deleteUserAccountFunction(data: StudentType) {
    const { userAccountId, username } = data;
    const params = { userAccountId, username };

    deleteUserAccount(params).then((res: any) => {
      if (!res.success) {
        return message.error(errorT.failed);
      }
      message.success(errorT.success);
      fetchStudentFunction();
    });
  };

  function resetUserPasswordFunction(data: StudentType) {
    const { username } = data;
    const params = { username };

    resetUserPassword(params).then((res: any) => {
      const { success, value } = res;
      const { newPassword } = value;
      if (!success) {
        return message.error(errorT.failed);
      }
      openNotification(newPassword);
    });
  };

  return (
    <>
      <Row gutter={[0, 12]}>
        <Col span={24}>
          <ResponsiveTable
            dataSource={studentArray}
            columns={columns}
            fixedRight={false}
          />
        </Col>
      </Row>

      {isModalChangeClassVisible &&
        <ReModal
          title={studentListT.CHANGE_CLASSROOM}
          onCancel={toggleChangeClassModal}
          visible={isModalChangeClassVisible}
          onOk={updateClassFunction}
          CollectedForm={ModalChangeClass}
        />
      }

      {isModalProfileVisible &&
        <ReModal
          title={profileInfoT.PROFILE_INFO}
          onCancel={toggleProfileModal}
          visible={isModalProfileVisible}
          onOk={(data: any) => getOnOkFunction(handleType, data, okUserFunction)}
          CollectedForm={ModalProfile}
          formProps={{
            getUserInfo,
            handleType,
          }}
          width={'700px'}
        />
      }

      {isModalSelfStudyVisible &&
        <ReModal
          title={classType}
          onCancel={() => setIsModalSelfStudyVisible(!isModalSelfStudyVisible)}
          visible={isModalSelfStudyVisible}
          onOk={updateStudyPeriodFunction}
          CollectedForm={ModalSelfStudy}
          formProps={{
            allPeriodArray,
            selfStudyInfo,
          }}
          width={'700px'}
        />
      }

      {isModalChangePasswordVisible &&
        <ReModal
          title={profileInfoT.CHANGE_PASSWORD}
          onCancel={() => setIsModalChangePasswordVisible(!isModalChangePasswordVisible)}
          visible={isModalChangePasswordVisible}
          onOk={updatePasswordFunction}
          CollectedForm={ModalChangePassword}
        />
      }
    </>
  )
}

export default StudentTable;